interface NodeProperties {
    [key: string]: any;
}

interface GraphNode {
    id: string;
    label: string;
    properties?: NodeProperties;
}

interface GraphLink {
    source: string;
    target: string;
    relationship: string;
}

export interface GraphData {
    nodes: GraphNode[];
    links: GraphLink[];
}

interface Neo4jNode {
    identity: {
        low: number;
        high: number;
    };
    labels: string[];
    properties: NodeProperties;
    elementId: string;
}

interface Neo4jRelationship {
    type: string;
}

interface Neo4jSegment {
    start: Neo4jNode;
    end: Neo4jNode;
    relationship: Neo4jRelationship;
}

interface Neo4jPath {
    start: Neo4jNode;
    end: Neo4jNode;
    segments: Neo4jSegment[];
}

interface Neo4jRecord {
    _fields: [Neo4jPath];
}

export function transformData(data: any): GraphData {
    try {
        console.log('Input data:', data);

        // Ensure data is an array
        const dataArray: Neo4jRecord[] = Array.isArray(data) ? data : [data];
        console.log('Processed data array length:', dataArray.length);

        const nodes = new Map<string, GraphNode>();
        const linksMap = new Map<string, GraphLink>();

        dataArray.forEach((entry: Neo4jRecord, index: number) => {
            console.log(`\nProcessing entry ${index + 1}/${dataArray.length}`);

            if (!entry?._fields?.[0]) {
                console.warn('Invalid entry structure:', entry);
                return;
            }

            const path = entry._fields[0];
            console.log('Path:', {
                hasStart: !!path?.start,
                hasEnd: !!path?.end,
                segmentsLength: path?.segments?.length
            });

            if (!path?.start || !path?.end) {
                console.warn('Invalid path structure:', path);
                return;
            }

            // Add start node
            if (path.start.elementId && !nodes.has(path.start.elementId)) {
                console.log('Adding start node:', {
                    id: path.start.identity?.low,
                    label: path.start.labels?.[0],
                    elementId: path.start.elementId
                });

                nodes.set(path.start.elementId, {
                    id: path.start.identity?.low?.toString() ?? path.start.elementId,
                    label: path.start.labels?.[0] ?? 'Unknown',
                    properties: path.start.properties ?? {}
                });
            }

            // Add end node
            if (path.end.elementId && !nodes.has(path.end.elementId)) {
                console.log('Adding end node:', {
                    id: path.end.identity?.low,
                    label: path.end.labels?.[0],
                    elementId: path.end.elementId
                });

                nodes.set(path.end.elementId, {
                    id: path.end.identity?.low?.toString() ?? path.end.elementId,
                    label: path.end.labels?.[0] ?? 'Unknown',
                    properties: path.end.properties ?? {}
                });
            }

            // Handle segments
            if (Array.isArray(path.segments)) {
                console.log(`Processing ${path.segments.length} segments`);

                path.segments.forEach((segment: Neo4jSegment, segIndex: number) => {
                    console.log(`\nProcessing segment ${segIndex + 1}/${path.segments.length}`);

                    if (!segment?.start?.elementId || !segment?.end?.elementId) {
                        console.warn('Invalid segment structure:', segment);
                        return;
                    }

                    // Add segment start node
                    if (!nodes.has(segment.start.elementId)) {
                        console.log('Adding segment start node:', {
                            id: segment.start.identity?.low,
                            label: segment.start.labels?.[0],
                            elementId: segment.start.elementId
                        });

                        nodes.set(segment.start.elementId, {
                            id: segment.start.identity?.low?.toString() ?? segment.start.elementId,
                            label: segment.start.labels?.[0] ?? 'Unknown',
                            properties: segment.start.properties ?? {}
                        });
                    }

                    // Add segment end node
                    if (!nodes.has(segment.end.elementId)) {
                        console.log('Adding segment end node:', {
                            id: segment.end.identity?.low,
                            label: segment.end.labels?.[0],
                            elementId: segment.end.elementId
                        });

                        nodes.set(segment.end.elementId, {
                            id: segment.end.identity?.low?.toString() ?? segment.end.elementId,
                            label: segment.end.labels?.[0] ?? 'Unknown',
                            properties: segment.end.properties ?? {}
                        });
                    }

                    // Add relationship
                    const linkKey = `${segment.start.elementId}-${segment.end.elementId}-${
                        segment.relationship?.type ?? 'Unknown'
                    }`;
                    if (!linksMap.has(linkKey)) {
                        console.log('Adding relationship:', {
                            source: segment.start.identity?.low,
                            target: segment.end.identity?.low,
                            type: segment.relationship?.type,
                            linkKey
                        });

                        linksMap.set(linkKey, {
                            source: segment.start.identity?.low?.toString() ?? segment.start.elementId,
                            target: segment.end.identity?.low?.toString() ?? segment.end.elementId,
                            relationship: segment.relationship?.type ?? 'Unknown'
                        });
                    }
                });
            } else if (path.start.elementId !== path.end.elementId) {
                // Handle direct relationship when no segments exist
                const linkKey = `${path.start.elementId}-${path.end.elementId}-DIRECT`;
                if (!linksMap.has(linkKey)) {
                    console.log('Adding direct relationship:', {
                        source: path.start.identity?.low,
                        target: path.end.identity?.low,
                        linkKey
                    });

                    linksMap.set(linkKey, {
                        source: path.start.identity?.low?.toString() ?? path.start.elementId,
                        target: path.end.identity?.low?.toString() ?? path.end.elementId,
                        relationship: 'DIRECT'
                    });
                }
            }
        });

        const result = {
            nodes: Array.from(nodes.values()),
            links: Array.from(linksMap.values())
        };

        console.log('\nFinal result:', {
            nodeCount: result.nodes.length,
            linkCount: result.links.length
        });

        return result;
    } catch (error) {
        console.error('Error transforming data:', error);
        return { nodes: [], links: [] };
    }
}
